import { useState, useEffect, useMemo, type ElementType } from 'react';
import { ArrowLeft, FileText, Zap, Database, ClipboardList, BarChart3, FileCheck, ShieldCheck } from 'lucide-react';
import { ProjectInfoForm } from '../ProjectInfoForm';
import { ServiceInfoForm } from '../ServiceInfoForm';
import { ExistingLoadStep } from '../ExistingLoadStep';
import { EquipmentSchedule } from '../EquipmentSchedule';
import { ResultsView } from '../ResultsView';
import { ReportView } from '../ReportView';
import { fetchFullAdminProject } from '../../lib/adminQueries';
import { calculateNEC22087 } from '../../lib/calculations';
import { enteredProposedLoads, enteredRemovedLoads } from '../../lib/calc/loadRoles';
import { canRunTraditional, runTraditionalCalc } from '../../lib/calc/runTraditional';
import { readingsInEffect } from '../../lib/peakExclusions';
import { validateProjectForCompletion } from '../../validation/projectCompletionValidation';
import type { Project, Equipment, AMIDataPoint } from '../../types/project';

type Tab = 'project' | 'service' | 'ami' | 'equipment' | 'results' | 'report';

const TABS: { id: Tab; label: string; Icon: ElementType }[] = [
  { id: 'project', label: 'Project Info', Icon: FileText },
  { id: 'service', label: 'Service Info', Icon: Zap },
  { id: 'ami', label: 'Existing Load', Icon: Database },
  { id: 'equipment', label: 'New Loads', Icon: ClipboardList },
  { id: 'results', label: 'Results', Icon: BarChart3 },
  { id: 'report', label: 'Report', Icon: FileCheck },
];

interface AdminProjectViewerProps {
  projectId: string;
  projectName: string;
  onClose: () => void;
}

export default function AdminProjectViewer({ projectId, projectName, onClose }: AdminProjectViewerProps) {
  const [activeTab, setActiveTab] = useState<Tab>('project');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [project, setProject] = useState<Partial<Project>>({});
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [amiData, setAmiData] = useState<AMIDataPoint[]>([]);
  // Which sub-path of the Existing Load step the admin is looking at. The
  // step derives a sensible start (the stored basis, else whichever path holds
  // data) and the switch lets the admin see the other one.
  // The readings the owner's calculation uses (lib/peakExclusions.ts).
  const amiInEffect = useMemo(() => readingsInEffect(amiData, project), [amiData, project]);
  const [existingLoadMode, setExistingLoadMode] = useState<'measured' | 'traditional' | null>(null);
  // An admin printing someone's report must see the same draft stamp the owner
  // would — otherwise the read-only surface is the one place an incomplete
  // evaluation still prints looking finished.
  const [isDraft, setIsDraft] = useState(false);

  useEffect(() => {
    let cancelled = false;
    validateProjectForCompletion(projectId, project, equipment, amiInEffect)
      .then(r => {
        if (!cancelled) setIsDraft(r.summary.blockerCount > 0);
      })
      .catch(() => {
        // A validation failure must not silently downgrade the stamp: assume
        // draft rather than assert a completeness we could not confirm.
        if (!cancelled) setIsDraft(true);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, project, equipment, amiInEffect]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchFullAdminProject(projectId)
      .then(({ project, equipment, amiData }) => {
        if (cancelled) return;
        setProject(project);
        setEquipment(equipment);
        setAmiData(amiData);
      })
      .catch(e => {
        if (!cancelled) setError(String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [projectId]);

  const canViewResults =
    project.service_rating_amps &&
    (project.max_recorded_demand_kw || amiData.length > 0 || canRunTraditional(project));

  // Memoized because this walks the whole interval series — ~35,000 points for
  // a typical project. Called bare in the render body it re-ran on every
  // render, including every tab switch and the isDraft state settling.
  const results = useMemo(
    // Proposed loads only — dwelling-inventory rows are traditional-path
    // inputs, not 220.87 new loads. The Equipment tab below still lists every
    // row: an admin viewer hides nothing.
    () => calculateNEC22087(project as Project, enteredProposedLoads(equipment), amiInEffect, enteredRemovedLoads(equipment)),
    [project, equipment, amiInEffect]
  );

  const traditionalResults = useMemo(
    () => (canRunTraditional(project) ? runTraditionalCalc(project, equipment) : null),
    [project, equipment]
  );
  // The owner's stored basis, honored read-only; measured when unset.
  const reportMethod: 'measured' | 'traditional' =
    project.report_method === 'traditional' && traditionalResults ? 'traditional' : 'measured';

  const noop = () => {};

  return (
    <div className="fixed inset-0 bg-gray-50 z-[60] flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Admin</span>
          </button>
          <div className="h-5 border-l border-gray-300" />
          <div className="flex items-center gap-2 min-w-0">
            <ShieldCheck className="w-5 h-5 text-purple-600 flex-shrink-0" />
            <span className="text-sm font-medium text-gray-500 flex-shrink-0">Admin View</span>
            <span className="text-gray-300 flex-shrink-0">·</span>
            <h1 className="text-base font-semibold text-gray-900 truncate">{projectName}</h1>
          </div>
        </div>
      </header>

      {/* Admin read-only banner */}
      <div className="bg-purple-50 border-b border-purple-200 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-3">
          <ShieldCheck className="w-4 h-4 text-purple-600 flex-shrink-0" />
          <p className="text-sm font-medium text-purple-800">
            Admin Read-Only View — changes cannot be made from this view
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mb-3" />
            <p className="text-gray-500 text-sm">Loading project…</p>
          </div>
        </div>
      ) : error ? (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md text-center">
            <p className="text-red-700 font-medium mb-1">Failed to load project</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      ) : (
        <>
          {/* Tab nav */}
          <nav className="bg-white border-b border-gray-200 flex-shrink-0">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex gap-1 overflow-x-auto">
                {TABS.map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === id
                        ? 'border-purple-600 text-purple-700'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </nav>

          {/* Content */}
          <main className="flex-1 overflow-y-auto">
            <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
              {activeTab === 'project' && (
                <ProjectInfoForm project={project} onChange={noop} isReadOnly />
              )}
              {activeTab === 'service' && (
                <ServiceInfoForm project={project} onChange={noop} isReadOnly />
              )}
              {/* The whole Existing Load step, read-only — both paths. Until
                  2026-09-02 this rendered the AMI input alone, so an admin
                  could not see a traditional project's occupancy, floor area,
                  inventory or elections at all (audit item S7). projectId is
                  null like the sibling ResultsView below: read-only mode
                  renders no consent UI, so a real id would only add a fetch
                  and a realtime channel whose topic collides with the editor's. */}
              {activeTab === 'ami' && (
                <ExistingLoadStep
                  projectId={null}
                  project={project}
                  amiDataCount={amiData.length}
                  amiData={amiData}
                  equipment={equipment}
                  onChange={noop}
                  onUploadCSV={noop}
                  onDeleteData={noop}
                  onAddExisting={noop}
                  onSplitExisting={noop}
                  onUpdateEquipment={noop}
                  onDeleteEquipment={noop}
                  isReadOnly
                  mode={existingLoadMode}
                  onModeChange={setExistingLoadMode}
                />
              )}
              {activeTab === 'equipment' && (
                <EquipmentSchedule
                  equipment={equipment}
                  onAdd={noop}
                  onUpdate={noop}
                  onDelete={noop}
                  isReadOnly
                />
              )}
              {activeTab === 'results' && canViewResults && (
                <ResultsView
                  project={project}
                  equipment={equipment}
                  amiData={amiInEffect}
                  projectId={null}
                  paymentStatus={{
                    subscription_status: 'active',
                    subscription_ends_at: null,
                    trial_used: false,
                    firm_subscription_status: null,
                    // Irrelevant here — the synthetic subscription_status above
                    // is what opens the platform admin's read-only view.
                    firm_has_seat: false,
                    has_saved_payment_method: false,
                    card_last4: null,
                    card_brand: null,
                    loading: false,
                    error: null,
                  }}
                  onPaymentStatusRefresh={() => {}}
                />
              )}
              {activeTab === 'report' && canViewResults && (
                <ReportView
                  project={project}
                  equipment={equipment}
                  amiData={amiInEffect}
                  results={results}
                  isDraft={isDraft}
                  method={reportMethod}
                  traditionalResults={traditionalResults ?? undefined}
                  measuredRan={!!(project.max_recorded_demand_kw || amiData.length > 0)}
                />
              )}
              {(activeTab === 'results' || activeTab === 'report') && !canViewResults && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                  <BarChart3 className="mx-auto text-gray-400 mb-4" size={64} />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Insufficient Data</h3>
                  <p className="text-gray-600 max-w-md mx-auto text-sm">
                    This project does not have enough data to display results. A service rating and
                    an established existing load are required — measured data (an AMI upload or a
                    manual peak demand) or a dwelling profile (traditional calculation).
                  </p>
                </div>
              )}
            </div>
          </main>
        </>
      )}
    </div>
  );
}
